import React, { useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SectionCard from "../SectionCard";
import InvButton from "../InvButton";
import { InvTextField } from "../InvField";
import InvItemAutocomplete from "../InvItemAutocomplete";
import { getItem } from "../../api/inventoryMaster";
import { getItemCostHistory } from "../../api/itemCosting";
import {
  emptyRfqLine,
  findDuplicateItemRows,
  pickLastPurchaseRate,
} from "../../utils/rfqEntity";
import { formatCurrency, formatQty } from "../../utils";
// import { emptyRfqLine } from "../../utils/rfqEntity";
import { defaultUom, itemGstPercent, resolveItemGst } from "../../utils/itemDisplay";
import { IPD_COLORS } from "../../theme";
const CELL = { py: 1.25, verticalAlign: "middle" };

async function fetchLastPurchaseRate(itemId) {
  if (!itemId) return null;
  try {
    const fromItem = pickLastPurchaseRate(await getItem(itemId));
    if (fromItem != null) return fromItem;
  } catch {
    // Item master may not expose a rate; try cost history next.
  }
  try {
    const hist = await getItemCostHistory(itemId);
    const rows = Array.isArray(hist)
      ? hist
      : hist?.items || hist?.results || hist?.history || hist?.data || [];
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const last = rows[0];
    return (
      pickLastPurchaseRate(last) ??
      pickLastPurchaseRate({
        last_purchase_rate: last?.newWac ?? last?.new_wac ?? last?.rate,
      })
    );
  } catch {
    return null;
  }
}

function applyLines(onChange, lines, updater) {
  onChange((prev) => updater(Array.isArray(prev) ? prev : lines));
}

export default function RfqLineItemsEditor({
  lines,
  onChange,
  getBaseUomCode,
  getPurchaseUomCode,
  getGstRate,
  defaultRequiredByDate = "",
  disabled = false,
}) {
  const fetchedRatesRef = useRef(new Set());
  const fetchedGstRef = useRef(new Set());

  function addLine(afterIndex) {
    const row = { ...emptyRfqLine() };
    if (afterIndex === undefined) {
      onChange([...lines, row]);
      return;
    }
    const next = [...lines];
    next.splice(afterIndex + 1, 0, row);
    onChange(next);
  }

  function removeLine(index) {
    if (lines.length <= 1) return;
    onChange(lines.filter((_, i) => i !== index));
  }

  function updateLine(index, patch) {
    onChange(lines.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function requiredByFallback() {
    if (!defaultRequiredByDate) return "";
    const text = String(defaultRequiredByDate);
    return text.includes("T") ? text.slice(0, 10) : text;
  }

  async function fillLastRate(index, itemId) {
    if (!itemId) return;
    const rate = await fetchLastPurchaseRate(itemId);
    fetchedRatesRef.current.add(itemId);
    applyLines(onChange, lines, (current) =>
      current.map((row, i) =>
        i === index && row.item_id === itemId
          ? { ...row, last_rate: rate ?? row.last_rate ?? null }
          : row,
      ),
    );
  }

  async function fillItemGst(index, itemId, item) {
    if (!itemId && !item) return;
    const gst = await resolveItemGst(itemId, item);
    if (itemId) fetchedGstRef.current.add(itemId);
    if (gst === '') return;
    applyLines(onChange, lines, (current) =>
      current.map((row, i) =>
        i === index && (!itemId || row.item_id === itemId)
          ? { ...row, gst_rate: gst }
          : row,
      ),
    );
  }

  function onItemPick(index, itemId, item) {
    const uomId = defaultUom(item, "purchase") || item?.uom_id || "";
    const uomCode =
      item?.purchase_uom_code ||
      getPurchaseUomCode?.(itemId) ||
      getBaseUomCode?.(itemId) ||
      item?.base_uom_code ||
      item?.uom_code ||
      "";
    const lastRate = pickLastPurchaseRate(item);
    updateLine(index, {
      item_id: itemId,
      item_name: item?.item_name || item?.item_short_name || "",
      item_code: item?.item_code || "",
      hsn: item?.hsn_code || item?.hsn || "",
      uom_id: uomId,
      uom_code: uomCode,
      required_by_date: lines[index]?.required_by_date || requiredByFallback(),
      stock_on_hand: item?.stock_on_hand ?? item?.current_stock ?? null,
      last_rate: lastRate,
      gst_rate: itemGstPercent(item) || getGstRate?.(itemId) || "",
    });
    if (itemId) fetchedRatesRef.current.add(itemId);
    if (itemId && lastRate == null) {
      fillLastRate(index, itemId);
    }
    fillItemGst(index, itemId, item);
  }

  useEffect(() => {
    lines.forEach((line, index) => {
      if (!line.item_id || line.last_rate != null) return;
      if (fetchedRatesRef.current.has(line.item_id)) return;
      fetchedRatesRef.current.add(line.item_id);
      fillLastRate(index, line.item_id);
    });
    // Intentionally keyed off item ids so we hydrate Last rate on edit load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.map((line) => line.item_id).join("|")]);

  useEffect(() => {
    lines.forEach((line, index) => {
      if (!line.item_id) return;
      const gstMissing = line.gst_rate === '' || line.gst_rate == null || String(line.gst_rate) === '0';
      if (!gstMissing || fetchedGstRef.current.has(line.item_id)) return;
      fetchedGstRef.current.add(line.item_id);
      fillItemGst(index, line.item_id, line);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.map((line) => line.item_id).join("|")]);

  useEffect(() => {
    if (!getPurchaseUomCode && !getBaseUomCode && !getGstRate) return;
    applyLines(onChange, lines, (current) => {
      let changed = false;
      const next = current.map((line) => {
        if (!line.item_id) return line;
        const code = getPurchaseUomCode?.(line.item_id) || getBaseUomCode?.(line.item_id) || "";
        const gst = getGstRate?.(line.item_id);
        let patch = line;
        if (code && line.uom_code !== code) {
          changed = true;
          patch = { ...patch, uom_code: code };
        }
        if (gst !== '' && gst != null && String(patch.gst_rate ?? '') !== String(gst)) {
          changed = true;
          patch = { ...patch, gst_rate: gst };
        }
        return patch;
      });
      return changed ? next : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.map((line) => line.item_id).join("|"), getPurchaseUomCode, getBaseUomCode, getGstRate]);

  const duplicateRows = findDuplicateItemRows(lines);
  const validCount = lines.filter(
    (l) => l.item_id && l.required_quantity,
  ).length;
  const totalUnits = lines.reduce(
    (sum, l) => sum + (Number(l.required_quantity) || 0),
    0,
  );
  const pricedLines = lines.filter(
    (l) => l.last_rate != null && l.required_quantity,
  );
  const estimatedValue = pricedLines.reduce(
    (sum, l) => sum + Number(l.last_rate) * Number(l.required_quantity || 0),
    0,
  );

  return (
    <SectionCard
      title="Line Items"
      subtitle="Products, quantities, and last purchase rate"
      actions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            {validCount} item{validCount !== 1 ? "s" : ""} ready
          </Typography>
          {!disabled && (
            <InvButton
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addLine()}
            >
              Add Row
            </InvButton>
          )}
        </Box>
      }
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 860,
            "& td, & th": {
              borderColor: IPD_COLORS.border,
              color: IPD_COLORS.textPrimary,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 36 }}>
                #
              </TableCell>
              <TableCell
                sx={{ color: IPD_COLORS.textSecondary, minWidth: 220 }}
              >
                Item *
              </TableCell>
              <TableCell
                sx={{ color: IPD_COLORS.textSecondary, minWidth: 160 }}
              >
                Specification
              </TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>
                Qty *
              </TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 72 }}>
                UOM
              </TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>
                Last rate
              </TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>
                Required By
              </TableCell>
              <TableCell
                sx={{ color: IPD_COLORS.textSecondary, minWidth: 100 }}
              >
                Remarks
              </TableCell>
              <TableCell sx={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  sx={{
                    textAlign: "center",
                    color: IPD_COLORS.textSecondary,
                    py: 4,
                  }}
                >
                  Add at least one item from Item Master
                </TableCell>
              </TableRow>
            ) : (
              lines.map((line, i) => (
                <TableRow key={i}>
                  <TableCell sx={CELL}>{i + 1}</TableCell>
                  <TableCell sx={CELL}>
                    <InvItemAutocomplete
                      value={line.item_id}
                      onChange={(id, item) => onItemPick(i, id, item)}
                      placeholder="Search item…"
                    />
                    {duplicateRows.has(i) && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: IPD_COLORS.error.text,
                          mt: 0.5,
                        }}
                      >
                        Already added to this RFQ — remove this row or pick a
                        different item.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.specification}
                      label=""
                      placeholder="Brand, grade, size…"
                      disabled={disabled}
                      onChange={(e) =>
                        updateLine(i, { specification: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.required_quantity}
                      label=""
                      type="number"
                      inputProps={{ min: 0, step: "any" }}
                      disabled={disabled}
                      onChange={(e) =>
                        updateLine(i, { required_quantity: e.target.value })
                      }
                    />
                    {line.stock_on_hand != null &&
                      Number(line.required_quantity) >
                        Number(line.stock_on_hand) && (
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: IPD_COLORS.warning.text,
                            mt: 0.5,
                          }}
                        >
                          Only {formatQty(line.stock_on_hand)} in stock
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...CELL,
                      color: IPD_COLORS.textSecondary,
                      fontSize: 13,
                    }}
                  >
                    {line.uom_code || "—"}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...CELL,
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {line.last_rate != null
                      ? formatCurrency(line.last_rate)
                      : "—"}
                    {line.last_rate != null && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 500,
                          color: IPD_COLORS.textSecondary,
                        }}
                      >
                        Last purchase
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.required_by_date}
                      label=""
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      disabled={disabled}
                      onChange={(e) =>
                        updateLine(i, { required_by_date: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.remarks}
                      label=""
                      disabled={disabled}
                      onChange={(e) =>
                        updateLine(i, { remarks: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell sx={CELL}>
                    {!disabled && (
                      <IconButton
                        size="small"
                        aria-label="Remove row"
                        disabled={lines.length <= 1}
                        onClick={() => removeLine(i)}
                        sx={{ color: IPD_COLORS.error.text }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {validCount > 0 && (
        <Box
          sx={{
            mt: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: "8px",
            backgroundColor: IPD_COLORS.selectedBg,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: IPD_COLORS.textPrimary,
              fontWeight: 600,
            }}
          >
            {validCount} item{validCount !== 1 ? "s" : ""} ·{" "}
            {formatQty(totalUnits)} units
            {pricedLines.length > 0 &&
              ` · estimated ${formatCurrency(estimatedValue)}`}
          </Typography>
          {pricedLines.length > 0 && pricedLines.length < validCount && (
            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
              Estimate covers {pricedLines.length} of {validCount} items — the
              rest have no purchase history.
            </Typography>
          )}
          {duplicateRows.size > 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: IPD_COLORS.error.text,
                fontWeight: 600,
              }}
            >
              {duplicateRows.size} duplicate item
              {duplicateRows.size !== 1 ? "s" : ""} to fix
            </Typography>
          )}
        </Box>
      )}
    </SectionCard>
  );
}
